import React, { useCallback, useState, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-react';
import { cn } from '../../utils/cn';
type ToastType = 'success' | 'error' | 'warning' | 'info';
type ToastPosition = 'top-right' | 'top-center' | 'bottom-right' | 'bottom-center';
interface ToastAction {
  label: string;
  onClick: () => void;
}
interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  action?: ToastAction;
  persistent?: boolean;
  showProgress?: boolean;
}
interface ToastContextType {
  showToast: (toast: Omit<Toast, 'id'>) => void;
  hideToast: (id: string) => void;
  position: ToastPosition;
  setPosition: (position: ToastPosition) => void;
}
const defaultContext: ToastContextType = {
  showToast: () => console.warn('ToastProvider not found'),
  hideToast: () => console.warn('ToastProvider not found'),
  position: 'top-right',
  setPosition: () => console.warn('ToastProvider not found')
};
const ToastContext = createContext<ToastContextType>(defaultContext);
export function useToast() {
  return useContext(ToastContext);
}
export function ToastProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [position, setPosition] = useState<ToastPosition>('top-right');
  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);
  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(7);
    const duration = toast.duration || 5000;
    const newToast = {
      ...toast,
      id,
      duration,
      showProgress: toast.showProgress ?? true
    };
    setToasts(prev => {
      const updated = [...prev, newToast];
      if (updated.length > 5) return updated.slice(updated.length - 5);
      return updated;
    });
    if (!toast.persistent) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    }
  }, []);
  const iconMap = {
    success: CheckCircle2,
    error: XCircle,
    warning: AlertCircle,
    info: Info
  };
  const colorMap = {
    success: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-500',
      icon: 'text-emerald-600',
      text: 'text-emerald-900',
      progress: 'bg-emerald-500'
    },
    error: {
      bg: 'bg-red-50',
      border: 'border-red-500',
      icon: 'text-red-600',
      text: 'text-red-900',
      progress: 'bg-red-500'
    },
    warning: {
      bg: 'bg-amber-50',
      border: 'border-amber-500',
      icon: 'text-amber-600',
      text: 'text-amber-900',
      progress: 'bg-amber-500'
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-500',
      icon: 'text-blue-600',
      text: 'text-blue-900',
      progress: 'bg-blue-500'
    }
  };
  const positionClasses = {
    'top-right': 'top-4 right-4 items-end',
    'top-center': 'top-4 left-1/2 -translate-x-1/2 items-center',
    'bottom-right': 'bottom-4 right-4 items-end',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2 items-center'
  };
  return <ToastContext.Provider value={{
    showToast,
    hideToast,
    position,
    setPosition
  }} data-id="element-313">
      {children}
      <div className={cn('fixed z-[100] flex flex-col gap-2 pointer-events-none', positionClasses[position])} data-id="element-314">
        <AnimatePresence mode="popLayout" data-id="element-315">
          {toasts.map(toast => {
          const Icon = iconMap[toast.type] || Info;
          const colors = colorMap[toast.type] || colorMap.info;
          return <motion.div key={toast.id} layout initial={{
            opacity: 0,
            y: position.includes('top') ? -20 : 20,
            scale: 0.9
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9,
            transition: {
              duration: 0.2
            }
          }} className={cn('relative overflow-hidden rounded-lg shadow-lg border pointer-events-auto min-w-[320px] max-w-md', colors.bg, colors.border)} data-id="element-316">
                <div className="p-4 flex items-start gap-3" data-id="element-317">
                  <Icon className={cn('w-5 h-5 flex-shrink-0 mt-0.5', colors.icon)} data-id="element-318" />
                  <div className="flex-1 min-w-0" data-id="element-319">
                    <h4 className={cn('text-sm font-semibold', colors.text)} data-id="element-320">
                      {toast.title}
                    </h4>
                    {toast.message && <p className={cn('text-sm opacity-90 mt-1', colors.text)} data-id="element-321">
                        {toast.message}
                      </p>}
                    {toast.action && <button onClick={e => {
                  e.stopPropagation();
                  toast.action?.onClick();
                  hideToast(toast.id);
                }} className={cn('mt-2 text-xs font-bold uppercase tracking-wide hover:underline focus:outline-none', colors.icon)} data-id="element-322">
                        {toast.action.label}
                      </button>}
                  </div>
                  <button onClick={() => hideToast(toast.id)} className={cn('hover:opacity-70 transition-opacity flex-shrink-0', colors.icon)} data-id="element-323">
                    <X className="w-4 h-4" data-id="element-324" />
                  </button>
                </div>

                {toast.showProgress && !toast.persistent && <motion.div initial={{
              width: '100%'
            }} animate={{
              width: '0%'
            }} transition={{
              duration: (toast.duration || 5000) / 1000,
              ease: 'linear'
            }} className={cn('h-1 absolute bottom-0 left-0', colors.progress)} data-id="element-325" />}
              </motion.div>;
        })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>;
}